/**
 * What the aftermath steps share: the environment they are handed, the view they return, the
 * box every step is drawn in, and the label-leader-value row the debrief and summary print.
 */

import type { RangerRecord } from '../../../meta/types';
import type { AftermathInput, Ctx } from '../context';
import type { Timers } from '../dom';
import { box, button, el } from '../dom';

export interface StepEnv {
  readonly ctx: Ctx;
  readonly input: AftermathInput;
  /**
   * The screen's own timeouts, cleared at every step and on dispose. Kept apart from
   * `ctx.timers` so a step can cancel what the last one scheduled, whenever the router clears its own.
   */
  readonly timers: Timers;
  /** The screen element: the memorial beat darkens all of it. */
  readonly screen: HTMLElement;
  /** The deployed ranger did not come back: no ceremony, and his medals are posthumous. */
  readonly deceased: boolean;
  /** The deployed ranger as he now stands; the promotion step replaces it after a perk choice. */
  ranger: RangerRecord | null;
}

export interface StepView {
  readonly el: HTMLElement;
  /** What takes focus when the step appears; null while its button is still held back. */
  readonly focus: HTMLElement | null;
  /** Undo anything the step did to the screen around it. */
  leave?(): void;
}

/** A step draws itself and calls `next` from its CONTINUE button. */
export type Step = (env: StepEnv, next: () => void) => StepView;

export interface StepShell {
  el: HTMLElement;
  content: HTMLElement;
  actions: HTMLElement;
}

/** Heading, scrolling content, one row of actions: the box every step is drawn in. */
export function stepShell(heading: string, extraClass = ''): StepShell {
  const content = el('div', 'menu-after-content menu-col menu-grow menu-scroll');
  const actions = el('div', 'menu-after-actions');
  const root = box(
    extraClass === '' ? 'menu-after-step' : `menu-after-step ${extraClass}`,
    el('div', 'menu-after-heading', heading),
    content,
    actions,
  );
  return { el: root, content, actions };
}

/** The one button a step ends on. */
export function continueButton(onClick: () => void, label = 'CONTINUE'): HTMLButtonElement {
  return button(label, onClick, 'menu-after-continue');
}

/** Holds `node` disabled for `ms`, then enables and focuses it: a finger still firing cannot skip ahead. */
export function holdInput(
  timers: Timers,
  node: HTMLButtonElement,
  ms: number,
  then: () => void = () => undefined,
): void {
  node.disabled = true;
  timers.later(ms, () => {
    node.disabled = false;
    node.focus();
    then();
  });
}

/** `LABEL ........ value`; hidden until `reveal` unless `shown`. */
export function scoreRow(label: string, value: string, extraClass = '', shown = false): HTMLElement {
  const row = el('div', extraClass === '' ? 'menu-score-row' : `menu-score-row ${extraClass}`);
  if (shown) row.classList.add('menu-row-in');
  row.append(
    el('span', 'menu-score-label', label),
    el('span', 'menu-score-leader'),
    el('span', 'menu-score-points', value),
  );
  return row;
}

/** Type the rows out one after another. */
export function revealRows(timers: Timers, rows: readonly HTMLElement[], intervalMs: number, index = 0): void {
  const row = rows[index];
  if (row === undefined) return;
  row.classList.add('menu-row-in');
  timers.later(intervalMs, () => revealRows(timers, rows, intervalMs, index + 1));
}
